import { api } from "./api.ts";
import { t } from "./i18n.ts";
import type { AiCapabilities, AiCapability } from "../../shared/aiCapabilities.ts";

/**
 * "May I offer this AI button?" - the app-wide answer, resolved once at boot (round 117).
 *
 * The app has ~20 buttons that spend an AI call, and until this module every one of them was
 * offered unconditionally: on a default selfhost setup with no key configured you could click
 * ✨ Draft, ✨ Generate blurbs, Analyze song or Generate retrospective and get a toast reading
 * whatever the provider SDK happened to throw. That is precisely what CLAUDE.md's "every knob is
 * a promise the app has to keep" rules out - a control the install cannot honor is shown
 * disabled WITH the reason, never left live to fail at click time. Round 62 got this right for
 * the image lane (`coverGenPayload`); this is the same shape for the other two capabilities.
 *
 * It is a BOOT FACT, not page state (STYLEGUIDE, "State: passing, sharing, mutating"): `app.ts`
 * awaits it once before the first navigation - one localhost round-trip, alongside `/api/meta` -
 * so `aiWall()` is a synchronous read at every call site and no island needs a subscription or a
 * loading branch. The only thing that changes it mid-session is the owner saving a key in
 * Settings → AI, which re-loads it with `force`.
 *
 * Fail-open on error, deliberately: an unreachable status endpoint must never gray out the whole
 * app's AI. The server gate is still the enforcement - this is only the honest surface.
 */

let cache: AiCapabilities | null = null;
let inflight: Promise<AiCapabilities | null> | null = null;

export async function loadAiStatus(force = false): Promise<AiCapabilities | null> {
	if (force) {
		cache = null;
		inflight = null;
	}
	if (cache != null) return cache;
	inflight ??= api<{ capabilities: AiCapabilities }>("/api/ai/status")
		.then(result => {
			cache = result.capabilities;
			return cache;
		})
		.catch(() => {
			// Fail open: leave the buttons alone and let the server answer for itself
			inflight = null;
			return null;
		});
	return inflight;
}

/**
 * Why this AI capability can't be used right now, in the reader's language - or `""` when it can.
 *
 * A STRING rather than a boolean because that is what a call site needs for both halves of the
 * pattern at once (`?disabled=${wall !== ""}` + `title=${wall}`), which is the shape round 61's
 * artist cap settled on (`capReason`). Empty while the status has not loaded, so a button is never
 * grayed on a guess.
 */
export function aiWall(capability: AiCapability): string {
	const state = cache?.[capability];
	if (state == null || state.available || state.reasonKey == null) return "";
	return t(state.reasonKey);
}

/** Convenience for the common case - most AI buttons in this app write text. */
export function aiTextWall(): string {
	return aiWall("text");
}

/**
 * ⚠ **AUDIO ANALYSIS HIDES INSTEAD OF GRAYING OUT, and it is the ONE capability in this app that
 * does** (owner, 2026-08-10, on moving the install to the Codex subscription lane).
 *
 * Every other AI control here is disabled-with-reason, which is the root file's "every knob is a
 * promise" rule and stays the default - do not copy this shape to the next dark button. Audio is
 * different on the one test that matters: **it has a working sibling six pixels to its right.**
 * "Analyze from lyrics" writes the SAME `song_analysis` column, satisfies every downstream consumer
 * identically, and needs only the text lane - so the reader loses no capability by not seeing the
 * audio one. A grayed control whose whole explanation is *use the other button* is a permanent
 * apology for something that is already handled.
 *
 * ⚠ **What makes it defensible is that the lane is dark for a reason NOBODY is about to fix.** A
 * subscription cannot buy it: neither agent CLI will upload a file, so audio analysis resolves from
 * an OpenAI API key and nothing else (`resolveAudioLane()`), and this install runs on a plan. In
 * saas the reader could not act on it at all - the wall is the operator's. **When Codex grows an
 * audio modality, delete this function and put the button back**, which is why the decision is one
 * predicate and not a condition sprinkled over two call sites.
 *
 * ⚠ **It inherits `aiWall`'s fail-open, and that is load-bearing here in a way it is not there.**
 * Before the status has loaded, `aiWall` returns `""` and this returns true - so a slow round-trip
 * shows a button that may then be disabled, never a feature that silently is not in the product.
 * **Hiding on a guess is the one failure this must not have.**
 */
export function audioAnalysisOffered(): boolean {
	return aiWall("audio") === "";
}
